<template>
    <div>
        <!-- Page Header -->
        <section class="bg-dark pt-5 pb-4">
            <div class="container py-5">
                <h1 class="text-light pt-1 pt-md-3 mb-4">
                    Webhooks
                </h1>
                <p class="text-light opacity-75 mb-0">
                    Connect to Zapier and other services with webhook subscriptions
                </p>
            </div>
        </section>
        <!-- / Page Header -->

        <!-- Webhooks Content -->
        <section class="position-relative bg-white rounded-xxl-4 zindex-5" style="margin-top: -30px;">
            <div class="container pt-4 pb-5 mb-md-4">
                <!-- Breadcrumb-->
                <breadcrumb :crumbs="crumbs" class="pb-4 my-2"></breadcrumb>

                <!-- Is Loading -->
                <div v-if="isLoading" class="text-center">
                    <spinner></spinner>
                </div>
                <!-- / Is Loading -->

                <!-- Webhooks Management -->
                <div v-if="!isLoading" class="row">
                    <div class="col-lg-8">
                        <!-- Create Webhook Form -->
                        <div v-if="showCreateForm" class="card border-0 shadow-sm mb-4">
                            <div class="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                                <h5 class="mb-0">Create New Webhook</h5>
                                <button @click="closeCreateForm" type="button" class="btn-close"></button>
                            </div>
                            <div class="card-body">
                                <form @submit.prevent="saveWebhook">
                                    <div class="row">
                                        <div class="col-md-6 mb-3">
                                            <label class="form-label">Name</label>
                                            <input v-model="form.name" type="text" class="form-control" 
                                                   placeholder="e.g., Job Notifications">
                                            <div class="form-text">Optional: Human-readable name for this webhook</div>
                                        </div>
                                        <div class="col-md-6 mb-3">
                                            <label class="form-label">Status</label>
                                            <div class="form-check form-switch">
                                                <input v-model="form.is_active" class="form-check-input" type="checkbox">
                                                <label class="form-check-label">Active</label>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="mb-3">
                                        <label class="form-label">Webhook URL <span class="text-danger">*</span></label>
                                        <input v-model="form.webhook_url" type="url" class="form-control" 
                                               placeholder="https://hooks.zapier.com/hooks/catch/123456/abc123/"
                                               required>
                                        <div class="form-text">The URL where webhooks will be sent</div>
                                    </div>

                                    <div class="mb-3">
                                        <label class="form-label">Events <span class="text-danger">*</span></label>
                                        <div class="row">
                                            <div v-for="event in availableEvents" :key="event.value" class="col-md-6 mb-2">
                                                <div class="form-check">
                                                    <input v-model="form.events" :value="event.value" 
                                                           class="form-check-input" type="checkbox" :id="'create-event-' + event.value">
                                                    <label class="form-check-label" :for="'create-event-' + event.value">
                                                        <strong>{{ event.label }}</strong>
                                                        <div class="text-muted fs-sm">{{ event.description }}</div>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="mb-3">
                                        <label class="form-label">Secret Key</label>
                                        <input v-model="form.secret_key" type="text" class="form-control" 
                                               placeholder="Optional: Custom secret for webhook signature">
                                        <div class="form-text">Optional: Custom secret for webhook signature verification</div>
                                    </div>

                                    <div class="d-flex gap-2">
                                        <button type="submit" class="btn btn-primary"
                                                :disabled="isSaving">
                                            <spinner v-if="isSaving" class="me-2"></spinner>
                                            Create Webhook
                                        </button>
                                        <button @click="closeCreateForm" type="button" class="btn btn-secondary">
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <!-- / Create Webhook Form -->

                        <!-- Webhooks List -->
                        <div class="card border-0 shadow-sm">
                            <div class="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                                <h5 class="mb-0">Webhook Subscriptions</h5>
                                <button @click="showCreateForm = true" class="btn btn-primary btn-sm">
                                    <i class="fi-plus me-2"></i>
                                    Add Webhook
                                </button>
                            </div>
                            <div class="card-body p-0">
                                <!-- No Webhooks -->
                                <div v-if="webhooks.length === 0" class="text-center py-5">
                                    <i class="fi-webhook text-muted fs-1 mb-3"></i>
                                    <h6 class="text-muted mb-2">No webhooks configured</h6>
                                    <p class="text-muted mb-3">Create your first webhook subscription to start receiving notifications.</p>
                                    <button @click="showCreateForm = true" class="btn btn-primary">
                                        <i class="fi-plus me-2"></i>
                                        Add Webhook
                                    </button>
                                </div>
                                <!-- / No Webhooks -->

                                <!-- Webhooks List -->
                                <div v-else class="list-group list-group-flush">
                                    <div v-for="webhook in webhooks" :key="webhook.id" class="list-group-item border-0 py-3">
                                        <!-- Edit Form (Inline) -->
                                        <div v-if="editingWebhook && editingWebhook.id === webhook.id" class="mb-3">
                                            <div class="card border-0 shadow-sm">
                                                <div class="card-header bg-transparent border-0 d-flex justify-content-between align-items-center">
                                                    <h6 class="mb-0">Edit Webhook</h6>
                                                    <button @click="cancelEdit" type="button" class="btn-close"></button>
                                                </div>
                                                <div class="card-body">
                                                    <form @submit.prevent="saveWebhook">
                                                        <div class="row">
                                                            <div class="col-md-6 mb-3">
                                                                <label class="form-label">Name</label>
                                                                <input v-model="form.name" type="text" class="form-control" 
                                                                       placeholder="e.g., Job Notifications">
                                                                <div class="form-text">Optional: Human-readable name for this webhook</div>
                                                            </div>
                                                            <div class="col-md-6 mb-3">
                                                                <label class="form-label">Status</label>
                                                                <div class="form-check form-switch">
                                                                    <input v-model="form.is_active" class="form-check-input" type="checkbox">
                                                                    <label class="form-check-label">Active</label>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div class="mb-3">
                                                            <label class="form-label">Webhook URL <span class="text-danger">*</span></label>
                                                            <input v-model="form.webhook_url" type="url" class="form-control" 
                                                                   placeholder="https://hooks.zapier.com/hooks/catch/123456/abc123/"
                                                                   required>
                                                            <div class="form-text">The URL where webhooks will be sent</div>
                                                        </div>

                                                        <div class="mb-3">
                                                            <label class="form-label">Events <span class="text-danger">*</span></label>
                                                            <div class="row">
                                                                <div v-for="event in availableEvents" :key="event.value" class="col-md-6 mb-2">
                                                                    <div class="form-check">
                                                                        <input v-model="form.events" :value="event.value" 
                                                                               class="form-check-input" type="checkbox" :id="'edit-event-' + event.value">
                                                                        <label class="form-check-label" :for="'edit-event-' + event.value">
                                                                            <strong>{{ event.label }}</strong>
                                                                            <div class="text-muted fs-sm">{{ event.description }}</div>
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div class="mb-3">
                                                            <label class="form-label">Secret Key</label>
                                                            <input v-model="form.secret_key" type="text" class="form-control" 
                                                                   placeholder="Optional: Custom secret for webhook signature">
                                                            <div class="form-text">Optional: Custom secret for webhook signature verification</div>
                                                        </div>

                                                        <div class="d-flex gap-2">
                                                            <button type="submit" class="btn btn-primary"
                                                                    :disabled="isSaving">
                                                                <spinner v-if="isSaving" class="me-2"></spinner>
                                                                Update Webhook
                                                            </button>
                                                            <button @click="cancelEdit" type="button" class="btn btn-secondary">
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </form>
                                                </div>
                                            </div>
                                        </div>
                                        <!-- / Edit Form (Inline) -->

                                        <!-- Webhook Display -->
                                        <div v-else class="d-flex justify-content-between align-items-start">
                                            <div class="flex-grow-1">
                                                <div class="d-flex align-items-center mb-2">
                                                    <h6 class="mb-0 me-2">{{ webhook.name || 'Unnamed Webhook' }}</h6>
                                                    <span v-if="webhook.is_active" class="badge bg-success bg-opacity-10 text-success">
                                                        Active
                                                    </span>
                                                    <span v-else class="badge bg-secondary bg-opacity-10 text-secondary">
                                                        Inactive
                                                    </span>
                                                </div>
                                                <p class="text-muted mb-1 fs-sm">{{ webhook.webhook_url }}</p>
                                                <div class="d-flex flex-wrap gap-1 mb-2">
                                                    <span v-for="event in webhook.events" :key="event" 
                                                          class="badge bg-primary bg-opacity-10 text-primary fs-xs">
                                                        {{ formatEventName(event) }}
                                                    </span>
                                                </div>
                                                <div class="d-flex align-items-center text-muted fs-sm">
                                                    <span v-if="webhook.last_triggered_at">
                                                        Last triggered: {{ formatDate(webhook.last_triggered_at) }}
                                                    </span>
                                                    <span v-else>
                                                        Never triggered
                                                    </span>
                                                    <span v-if="webhook.failure_count > 0" class="ms-3 text-danger">
                                                        {{ webhook.failure_count }} failures
                                                    </span>
                                                </div>
                                            </div>
                                            <div class="flex-shrink-0 ms-3">
                                                <div class="btn-group btn-group-sm">
                                                    <button @click="testWebhook(webhook)" 
                                                            class="btn btn-outline-primary"
                                                            :disabled="isTesting === webhook.id">
                                                        <spinner v-if="isTesting === webhook.id" class="me-1" style="width: 12px; height: 12px;"></spinner>
                                                        <i v-else class="fi-send me-1"></i>
                                                        Test
                                                    </button>
                                                    <button @click="editWebhook(webhook)" 
                                                            class="btn btn-outline-primary">
                                                        <i class="fi-edit"></i>
                                                    </button>
                                                    <button @click="deleteWebhook(webhook)" 
                                                            class="btn btn-outline-danger">
                                                        <i class="fi-trash"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <!-- / Webhook Display -->
                                    </div>
                                </div>
                                <!-- / Webhooks List -->
                            </div>
                        </div>
                        <!-- / Webhooks List -->
                    </div>

                    <!-- Sidebar -->
                    <div class="col-lg-4">
                        <div class="card border-0 shadow-sm">
                            <div class="card-body p-4">
                                <h5 class="mb-3">Available Events</h5>
                                <ul class="list-unstyled mb-3">
                                    <li v-for="event in availableEvents" :key="event.value" class="mb-1">
                                        <i class="fi-check text-success me-2"></i>
                                        {{ event.label }}
                                    </li>
                                </ul>

                                <hr class="my-4">

                                <div class="mb-3">
                                    <h6 class="mb-2">How To Use</h6>
                                    <p class="mb-3">
                                        Learn how to set up and configure webhooks
                                    </p>
                                    <button @click="showHowToModal = true" class="btn btn-outline-primary mb-3">
                                        <i class="fi-info-circle me-2"></i>
                                        How To Use
                                    </button>
                                </div>

                                <hr class="my-4">

                                <div class="mb-3">
                                    <h6 class="mb-2">Need Help?</h6>
                                    <p class="mb-3">
                                        Having trouble setting up webhooks?
                                    </p>
                                    <a href="https://syndesi.network/contact/" 
                                       target="_blank" 
                                       class="btn btn-primary">
                                        <i class="fi-help me-2"></i>
                                        Contact Support
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- / Sidebar -->
                </div>
                <!-- / Webhooks Management -->


            </div>
        </section>
        <!-- / Webhooks Content -->

        <!-- How To Use Modal -->
        <div v-if="showHowToModal" class="modal show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">How To Use Webhooks</h5>
                        <button @click="showHowToModal = false" type="button" class="btn-close"></button>
                    </div>
                    <div class="modal-body p-0">
                        <iframe 
                            src="https://docs.google.com/document/d/1FGLU0h85Bpd9afJ6_6DwebEtqch-kw1NlDPbAei2vIU/preview?usp=sharing"
                            width="100%" 
                            height="600px"
                            frameborder="0"
                            style="border-radius: 0.375rem;">
                        </iframe>
                    </div>
                    <div class="modal-footer">
                        <button @click="showHowToModal = false" type="button" class="btn btn-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
        <!-- / How To Use Modal -->
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from '../../components/Spinner.vue';
import Breadcrumb from '../../components/Breadcrumb.vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';

const toast = useToast();

export default {
    name: 'WebhooksIntegration',
    
    components: { 
        Spinner, 
        Breadcrumb 
    },

    data() {
        return {
            isLoading: false,
            isSaving: false,
            isTesting: null,
            webhooks: [],
            showCreateForm: false,
            editingWebhook: null,
            showHowToModal: false,
            form: {
                name: '',
                webhook_url: '',
                events: [],
                secret_key: '',
                is_active: true
            },
            availableEvents: [
                {
                    value: 'job.created',
                    label: 'Job Created',
                    description: 'When a new job is created'
                },
                {
                    value: 'job.updated',
                    label: 'Job Updated',
                    description: 'When a job is updated'
                },
                {
                    value: 'job.deleted',
                    label: 'Job Deleted',
                    description: 'When a job is deleted'
                },
                {
                    value: 'quote.created',
                    label: 'Quote Created',
                    description: 'When a quote is created'
                },
                {
                    value: 'quote.accepted',
                    label: 'Quote Accepted',
                    description: 'When a quote is accepted'
                }
            ]
        }
    },

    computed: {
        ...mapGetters('auth', {
            user: 'user',
            userRole: 'userRole',
            isAdvisor: 'isAdvisor'
        }),

        crumbs() {
            return [
                {
                    href: "/",
                    label: "Dashboard",
                },
                {
                    href: "/integrations",
                    label: "Integrations",
                },
                {
                    href: "#",
                    label: "Webhooks",
                    active: true,
                },
            ];
        }
    },

    created() {
        // Check if user is an advisor, if not redirect
        if (!this.isAdvisor) {
            this.$router.push('/');
            return;
        }

        this.loadWebhooks();
    },

    methods: {
        async loadWebhooks() {
            this.isLoading = true;
            try {
                const response = await axios.get(`${window.API_BASE}/webhooks/subscriptions`, {
                    headers: {
                        'Authorization': `Bearer ${this.$store.getters['auth/apiToken']}`
                    }
                });
                this.webhooks = response.data.subscriptions || [];
            } catch (error) {
                console.error('Failed to load webhooks:', error);
                toast.error('Failed to load webhook subscriptions');
            } finally {
                this.isLoading = false;
            }
        },

        editWebhook(webhook) {
            this.editingWebhook = webhook;
            this.form = {
                name: webhook.name || '',
                webhook_url: webhook.webhook_url,
                events: [...webhook.events],
                secret_key: webhook.secret_key || '',
                is_active: webhook.is_active
            };
        },

        closeCreateForm() {
            this.showCreateForm = false;
            this.resetForm();
        },

        cancelEdit() {
            this.editingWebhook = null;
            this.resetForm();
        },

        resetForm() {
            this.form = {
                name: '',
                webhook_url: '',
                events: [],
                secret_key: '',
                is_active: true
            };
        },

        async saveWebhook() {
            if (!this.form.webhook_url || this.form.events.length === 0) {
                toast.error('Please provide a webhook URL and select at least one event');
                return;
            }

            this.isSaving = true;
            try {
                if (this.editingWebhook) {
                    await axios.put(`${window.API_BASE}/webhooks/subscriptions/${this.editingWebhook.id}`, this.form, {
                        headers: {
                            'Authorization': `Bearer ${this.$store.getters['auth/apiToken']}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    toast.success('Webhook updated successfully');
                    this.editingWebhook = null;
                } else {
                    await axios.post(`${window.API_BASE}/webhooks/subscriptions`, this.form, {
                        headers: {
                            'Authorization': `Bearer ${this.$store.getters['auth/apiToken']}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    toast.success('Webhook created successfully');
                    this.closeCreateForm();
                }
                
                this.loadWebhooks();
            } catch (error) {
                console.error('Failed to save webhook:', error);
                toast.error('Failed to save webhook');
            } finally {
                this.isSaving = false;
            }
        },

        async deleteWebhook(webhook) {
            if (!confirm('Are you sure you want to delete this webhook subscription?')) {
                return;
            }

            try {
                await axios.delete(`${window.API_BASE}/webhooks/subscriptions/${webhook.id}`, {
                    headers: {
                        'Authorization': `Bearer ${this.$store.getters['auth/apiToken']}`
                    }
                });
                toast.success('Webhook deleted successfully');
                this.loadWebhooks();
            } catch (error) {
                console.error('Failed to delete webhook:', error);
                toast.error('Failed to delete webhook');
            }
        },

        async testWebhook(webhook) {
            this.isTesting = webhook.id;
            try {
                await axios.post(`${window.API_BASE}/webhooks/subscriptions/${webhook.id}/test`, {}, {
                    headers: {
                        'Authorization': `Bearer ${this.$store.getters['auth/apiToken']}`
                    }
                });
                toast.success('Test webhook sent successfully');
            } catch (error) {
                console.error('Failed to test webhook:', error);
                toast.error('Failed to send test webhook');
            } finally {
                this.isTesting = null;
            }
        },

        formatEventName(event) {
            const eventMap = {
                'job.created': 'Job Created',
                'job.updated': 'Job Updated',
                'job.deleted': 'Job Deleted',
                'quote.created': 'Quote Created',
                'quote.accepted': 'Quote Accepted'
            };
            return eventMap[event] || event;
        },

        formatDate(dateString) {
            if (!dateString) return 'Never';
            return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
        }
    }
}
</script>

<style scoped>
.modal {
    z-index: 1050;
}

.list-group-item:hover {
    background-color: #f8f9fa;
}
</style> 